// Phase 9.1 — Reorder analytics dashboard.
//
// 6 status chips (clickable filters) + scope toggle (company vs
// location) + paginated table. Server-side filters (`status`,
// `category`, `search`, `scope`, `location`) — no client-side
// re-filtering to avoid drift.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API, detailFromError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { STATUS_ORDER, StatusPill, StatusChip, fmtNum } from "./statusMeta";

const PAGE_SIZE = 50;
// Phase 9.3 — localStorage key for the "Use calculated levels" toggle.
// (Pre-existing reference was undefined; declaring it here fixes the
// silent try/catch failure without changing behavior.)
const USE_CALC_LS_KEY = "fnb.reorder.use_calc";

function buildAutoSuggestLink({ scope, locationId, statusFilter, category }) {
  const p = new URLSearchParams();
  if (scope === "location" && locationId) {
    p.set("scope", "location");
    p.set("location", locationId);
  } else {
    p.set("scope", "company");
  }
  if (statusFilter) p.set("status", statusFilter);
  if (category) p.set("category", category);
  return `/tools/fnb-po/pos/auto?${p}`;
}

export default function FnbPoReorder() {
  // Phase 9.4b — respect ?item_scope=... in the URL (used when the
  // dashboard's "Bar Stock →" link or the Bar Stock page's "Open
  // reorder view" button navigates here). Default is `daily`.
  const [searchParams] = useSearchParams();
  const initialItemScope = (() => {
    const raw = searchParams.get("item_scope");
    return ["daily", "bar", "all"].includes(raw) ? raw : "daily";
  })();
  const [itemScope, setItemScope] = useState(initialItemScope);
  const [useCalc, setUseCalc] = useState(() => {
    try { return window.localStorage.getItem(USE_CALC_LS_KEY) === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(USE_CALC_LS_KEY, useCalc ? "1" : "0"); } catch {}
  }, [useCalc]);
  const [scope, setScope] = useState("company");
  const [locationId, setLocationId] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showNotApplicable, setShowNotApplicable] = useState(false);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Load active locations once for the scope dropdown
  useEffect(() => {
    let cancelled = false;
    api.get("/fnb/locations").then((res) => {
      if (!cancelled) {
        setLocations(res.data.filter((l) => l.active !== false));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    // When scope=location but no location picked yet, don't fire the
    // request — avoids a 400 toast until the user picks one.
    if (scope === "location" && !locationId) {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        scope, limit: PAGE_SIZE, offset,
      });
      params.set("item_scope", itemScope);
      if (scope === "location" && locationId) params.set("location", locationId);
      if (statusFilter) params.set("status", statusFilter);
      if (category) params.set("category", category);
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (showNotApplicable || statusFilter === "not_applicable") {
        params.set("include_not_applicable", "true");
      }
      const res = await api.get(`/fnb/analytics/reorder-status?${params}`);
      setData(res.data);
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setLoading(false);
    }
  }, [scope, locationId, statusFilter, category, debouncedSearch, offset, showNotApplicable, itemScope]);

  useEffect(() => { load(); }, [load]);

  // Reset offset when filters change
  useEffect(() => { setOffset(0); }, [scope, locationId, statusFilter, category, debouncedSearch, showNotApplicable, itemScope]);

  const aggregates = data?.aggregates || {};
  const items = data?.items || [];
  const total = data?.total || 0;

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + items.length, total);

  const csvHref = useMemo(() => {
    const params = new URLSearchParams({
      scope, format: "csv",
    });
    params.set("item_scope", itemScope);
    if (scope === "location" && locationId) params.set("location", locationId);
    if (statusFilter) params.set("status", statusFilter);
    if (category) params.set("category", category);
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (showNotApplicable || statusFilter === "not_applicable") {
      params.set("include_not_applicable", "true");
    }
    return `${API}/fnb/analytics/reorder-status?${params}`;
  }, [scope, locationId, statusFilter, category, debouncedSearch, showNotApplicable, itemScope]);

  return (
    <div className="space-y-6" data-testid="fnb-po-reorder">
      <header className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="reorder-heading">
            Reorder Dashboard
          </h1>
          <p className="text-sm text-muted-foreground">
            {useCalc ? (
              <>Calculated levels (consumption-based) drive status when available; falls back to sheet-defined values.</>
            ) : (
              <>Phase 9.7.1 — File-driven reorder rules from <code className="font-mono">fb_item_ordering_config</code> drive status. Items without a file rule show as <b>Unconfigured</b>. Toggle above to preview consumption-based calculated levels.</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label
            className="inline-flex items-center gap-2 text-xs cursor-pointer select-none px-2 py-1 rounded-md border border-border/70 hover:bg-muted transition-colors"
            data-testid="reorder-use-calc-toggle-label"
          >
            <input
              type="checkbox"
              checked={useCalc}
              onChange={(e) => setUseCalc(e.target.checked)}
              data-testid="reorder-use-calc-toggle"
            />
            Use calculated levels
          </label>
          <span
            className="text-[10px] text-muted-foreground cursor-help underline underline-offset-4 decoration-dotted"
            title="Phase 9.5 formula — reorder = max(adc_30 × TAT × 1.5, adc_7 × TAT × 2). adc_N = average daily consumption over the trailing N days from BOM-mapped sales."
            data-testid="reorder-formula-tooltip"
          >
            (?)
          </span>
          <Button asChild size="sm" data-testid="reorder-generate-po-btn">
            <Link to={buildAutoSuggestLink({ scope, locationId, statusFilter, category })}>
              Generate PO from these items →
            </Link>
          </Button>
          <a
            href={csvHref}
            download
            className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-border/70 text-sm font-medium hover:bg-muted transition-colors"
            data-testid="reorder-csv-export"
          >
            ↓ Download CSV
          </a>
          <Button asChild variant="ghost" size="sm" data-testid="reorder-back">
            <Link to="/tools/fnb-po">← Dashboard</Link>
          </Button>
        </div>
      </header>

      {/* Scope toggle */}
      <Card className="border-border/70">
        <CardContent className="pt-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              Scope
            </span>
            <div className="flex rounded-md border border-border/70 overflow-hidden">
              <button
                className={`px-3 py-1.5 text-xs font-medium transition ${
                  scope === "company"
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-muted"
                }`}
                onClick={() => setScope("company")}
                data-testid="scope-company-btn"
              >
                Company-wide
              </button>
              <button
                className={`px-3 py-1.5 text-xs font-medium transition border-l border-border/70 ${
                  scope === "location"
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-muted"
                }`}
                onClick={() => setScope("location")}
                data-testid="scope-location-btn"
              >
                Per-location
              </button>
            </div>
          </div>
          {scope === "location" && (
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger
                className="w-72 text-xs"
                data-testid="reorder-location-select"
              >
                <SelectValue placeholder="Pick a location…" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((l) => (
                  <SelectItem key={l.location_id} value={l.location_id} className="text-xs">
                    {l.display_name} <span className="text-muted-foreground">({l.location_id})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {data?.snapshot_uploaded_at && (
            <div className="text-[11px] text-muted-foreground ml-auto">
              Snapshot: <code className="font-mono">{data.snapshot_id?.slice(0, 16)}…</code>{" "}
              from {new Date(data.snapshot_uploaded_at).toLocaleDateString()}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status chips */}
      <section
        className="flex flex-wrap gap-2"
        data-testid="reorder-status-chips"
      >
        {STATUS_ORDER.map((s) => (
          <StatusChip
            key={s}
            status={s}
            count={aggregates[s] || 0}
            active={statusFilter === s}
            onClick={() => setStatusFilter(statusFilter === s ? null : s)}
            testid={`status-chip-${s}`}
          />
        ))}
        {statusFilter && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setStatusFilter(null)}
            data-testid="clear-status-filter"
            className="text-xs"
          >
            Clear filter ×
          </Button>
        )}
      </section>

      {/* Extra filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search SKU or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs text-sm"
          data-testid="reorder-search"
        />
        <Input
          placeholder="Filter by category…"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="max-w-xs text-sm"
          data-testid="reorder-category-filter"
        />
        <label className="flex items-center gap-2 text-xs cursor-pointer px-3 py-2 border border-border/70 rounded-md hover:bg-muted/40">
          <input
            type="checkbox"
            checked={showNotApplicable}
            onChange={(e) => setShowNotApplicable(e.target.checked)}
            className="h-3.5 w-3.5"
            data-testid="reorder-show-not-applicable"
          />
          Show N/A items
        </label>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <b>{total.toLocaleString()}</b> item{total === 1 ? "" : "s"}
              {statusFilter && (
                <> matching <StatusPill status={statusFilter} /></>
              )}
              {scope === "location" && locationId && (
                <> at <code className="font-mono text-xs">{locationId}</code></>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              Showing {pageStart}–{pageEnd}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && !data ? (
            <div className="text-sm text-muted-foreground" data-testid="reorder-loading">
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center" data-testid="reorder-empty">
              No items match these filters. Try clearing them or upload a stock CSV first.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table data-testid="reorder-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>UOM</TableHead>
                      <TableHead className="text-right">Current</TableHead>
                      <TableHead className="text-right">Reorder</TableHead>
                      <TableHead className="text-right">Danger</TableHead>
                      <TableHead className="text-right">Max</TableHead>
                      <TableHead className="text-right">TAT</TableHead>
                      <TableHead className="text-right">
                        <span title="Calculated levels — Phase 9.3 consumption-based">Calc R/D/M</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((r) => (
                      <TableRow
                        key={r.sku}
                        className="hover:bg-muted/30 cursor-pointer"
                        data-testid={`reorder-row-${r.sku}`}
                      >
                        <TableCell>
                          <StatusPill status={r.status} testid={`row-status-${r.sku}`} />
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          <Link
                            to={`/tools/fnb-po/items/${encodeURIComponent(r.sku)}`}
                            className="text-primary underline underline-offset-4"
                            data-testid={`row-sku-link-${r.sku}`}
                          >
                            {r.sku}
                          </Link>
                        </TableCell>
                        <TableCell className="text-sm">{r.name || "—"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {r.category || "—"}
                        </TableCell>
                        <TableCell className="text-sm">{r.uom || "—"}</TableCell>
                        <TableCell className="text-right font-medium">
                          {fmtNum(r.current_stock_total)}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {(() => {
                            const cs = r.calculated_levels;
                            if (useCalc && cs?.reorder != null) return fmtNum(cs.reorder);
                            // Phase 9.7.1 — prefer file-driven `file_reorder_level` when present;
                            // else fall back to legacy sheet_levels.reorder.
                            if (r.file_reorder_level != null) return fmtNum(r.file_reorder_level);
                            return fmtNum(r.sheet_levels?.reorder);
                          })()}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {(() => {
                            const cs = r.calculated_levels;
                            return (useCalc && cs?.danger != null)
                              ? fmtNum(cs.danger) : fmtNum(r.sheet_levels?.danger);
                          })()}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {(() => {
                            const cs = r.calculated_levels;
                            return (useCalc && cs?.max != null)
                              ? fmtNum(cs.max) : fmtNum(r.sheet_levels?.max);
                          })()}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {fmtNum(r.tat_days)}
                        </TableCell>
                        <TableCell className="text-right text-xs">
                          {r.calculated_levels?.reorder != null ? (
                            <span
                              className="tabular-nums"
                              title={`ADC ${r.calculated_levels.avg_daily_consumption}/day · MDC ${r.calculated_levels.max_daily_consumption}/day · ${r.calculated_levels.coverage_pct}% coverage`}
                              data-testid={`row-calc-${r.sku}`}
                            >
                              {fmtNum(r.calculated_levels.reorder)} / {fmtNum(r.calculated_levels.danger)} / {fmtNum(r.calculated_levels.max)}
                            </span>
                          ) : r.calculated_levels?.no_bom ? (
                            <span className="text-muted-foreground italic" title="No BOM references this SKU yet">no BOM</span>
                          ) : (
                            <span className="text-muted-foreground italic" title={r.calculated_levels?.reason || ""}>—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-between pt-4">
                <div className="text-xs text-muted-foreground">
                  Page {Math.floor(offset / PAGE_SIZE) + 1} of{" "}
                  {Math.max(1, Math.ceil(total / PAGE_SIZE))}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    data-testid="reorder-prev-page"
                  >
                    ← Prev
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={offset + items.length >= total}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                    data-testid="reorder-next-page"
                  >
                    Next →
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
